import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'
import ReactDOM from 'react-dom'
import { v4 as uuidv4 } from 'uuid'

import { Icon } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { UI2PlatformContext } from '../../forms/ui2-form/ui2-platform-context'
import { UI2FileViewer, UI2FileViewerLightbox } from 'browser/components/ui2/fields/field-ui/file-viewer/file-viewer'
import { FramesManager } from 'shared-libs/components/view/frames-manager'
import {
  convertToWebp,
  getFileMimeType,
  getFileType,
  resizeImageFile,
} from 'browser/app/utils/image'

import {
  UI2FileUploadFieldProps,
  UI2_FILE_UPLOAD_FIELD_DEFAULT_PROPS,
} from 'shared-libs/components/ui2/fields/ui2-file-upload-field/ui2-file-upload-field-props'
import {
  FileCountBounds,
  getFileEntryName,
  getFileEntryUri,
  isFileCountRequired,
  isImageFileEntry,
  resolveFileCountBounds,
  withFileCountError,
} from 'shared-libs/components/ui2/fields/core-field/media-attachment-field-props'
import { UI2FieldShell, IUI2FieldShellProps } from '../field-ui/field-shell/field-shell'
import { UI2FieldControl } from '../field-ui/field-control/field-control'

import './_ui2-file-upload-field.scss'

const DEFAULT_EMPTY_MESSAGE_DW = 'Click to upload or drag files here'
const DEFAULT_EMPTY_MESSAGE_MW = 'Tap to upload files'
const DEFAULT_EMPTY_BUTTON_TEXT = 'Upload file'
const DEFAULT_EMPTY_READ_ONLY_TEXT = 'No files available'
const DEFAULT_PREFILLED_EDIT_TEXT = 'Add/Remove Files'
const DEFAULT_ADD_MORE_TEXT = 'Add files'
const DEFAULT_MAX_SHORT_EDGE = 2350
const DEFAULT_WEBP_QUALITY = 0.75

interface IUI2FileUploadFieldWebExtras extends Omit<IBaseProps, 'children'> {
  onChange?: (value: any) => void
  errors?: any[]
  frames?: FramesManager
  translationTable?: any
}

export interface IUI2FileUploadFieldProps
  extends UI2FileUploadFieldProps,
    IUI2FileUploadFieldWebExtras {}

interface IUI2FileUploadFieldState {
  isDragging: boolean
  lightboxIndex: number | null
}

export class UI2FileUploadField extends React.Component<IUI2FileUploadFieldProps, IUI2FileUploadFieldState> {
  static contextType = UI2PlatformContext
  declare context: React.ContextType<typeof UI2PlatformContext>

  public static defaultProps: Partial<IUI2FileUploadFieldProps> = {
    ...UI2_FILE_UPLOAD_FIELD_DEFAULT_PROPS,
    addMoreMessage: DEFAULT_ADD_MORE_TEXT,
    maxImageDimension: DEFAULT_MAX_SHORT_EDGE,
    imageQuality: DEFAULT_WEBP_QUALITY,
  }

  public state: IUI2FileUploadFieldState = { isDragging: false, lightboxIndex: null }

  protected inputRef = React.createRef<HTMLInputElement>()
  private dragDepth = 0

  constructor(props: IUI2FileUploadFieldProps) {
    super(props)
  }

  public render(): React.ReactNode {
    const { className, style } = this.props
    const files = this.getValue()
    const isEmpty = _.isEmpty(files)
    return (
      <>
        <UI2FieldShell
          {...(this.props as IUI2FieldShellProps<any[]>)}
          // A positive `minFiles` makes the field required in every sense the
          // shell cares about — asterisk, submit-time gate — so the count
          // error below only has to cover the partially-filled case.
          isRequired={isFileCountRequired(this.props)}
          errors={withFileCountError(this.props.errors, files.length, this.props)}
          isValueEmpty={UI2FileUploadField.isValueEmpty}
          emptyText={this.props.emptyText ?? this.defaultEmptyReadOnlyText()}
          className={classNames('ui2-fileUploadField', className, {
            'ui2-fileUploadField--isEmpty': isEmpty,
          })}
          style={style}
          prefilledEditText={this.defaultPrefilledEditText()}
          bodyIsFieldControl={true}
          renderReadOnly={this.renderReadOnly}
        >
          <UI2FieldControl>{this.renderBody()}</UI2FieldControl>
        </UI2FieldShell>
        {this.renderLightbox()}
      </>
    )
  }

  private static isValueEmpty = (value: any[] | undefined): boolean => {
    if (_.isArray(value)) return _.isEmpty(value)
    const filesetFiles = _.get(value, 'files')
    if (_.isArray(filesetFiles)) return _.isEmpty(filesetFiles)
    return true
  }

  private renderLightbox(): React.ReactNode {
    const { lightboxIndex } = this.state
    if (lightboxIndex === null || typeof document === 'undefined') return null
    // The lightbox renders an <img>, so it only ever holds the image entries —
    // indices below are into this list, not into the full value.
    const files = this.getImageFiles()
    if (_.isEmpty(files)) return null
    const total = files.length
    const index = Math.min(lightboxIndex, total - 1)
    const current = files[index]
    return ReactDOM.createPortal(
      <UI2FileViewerLightbox
        images={files}
        index={index}
        current={current}
        total={total}
        onPrev={this.handleLightboxPrev}
        onNext={this.handleLightboxNext}
        onClose={this.handleLightboxClose}
      />,
      document.body,
    )
  }

  /** Positional entry point kept for subclasses that render by index. */
  protected handleChipClick = (index: number) => {
    const file = this.getValue()[index]
    if (file) this.handleOpenFile(file)
  }

  /**
   * The one gate into previewing a file. Images open the lightbox at their
   * own position within the image subset (so every image is reachable, not
   * just the first); anything the lightbox can't render — a PDF, a CSV —
   * opens in a new tab instead. A disabled field previews nothing.
   */
  protected handleOpenFile = (file: any) => {
    if (this.props.isDisabled) return
    if (isImageFileEntry(file)) {
      const imageIndex = this.getImageFiles().indexOf(file)
      this.setState({ lightboxIndex: Math.max(0, imageIndex) })
      return
    }
    const uri = getFileEntryUri(file)
    if (uri) window.open(uri, '_blank', 'noopener,noreferrer')
  }

  private handleLightboxClose = () => {
    this.setState({ lightboxIndex: null })
  }

  private handleLightboxPrev = () => {
    this.setState(({ lightboxIndex }) => ({
      lightboxIndex: lightboxIndex === null ? null : Math.max(0, lightboxIndex - 1),
    }))
  }

  private handleLightboxNext = () => {
    const total = this.getImageFiles().length
    this.setState(({ lightboxIndex }) => ({
      lightboxIndex: lightboxIndex === null ? null : Math.min(total - 1, lightboxIndex + 1),
    }))
  }

  private renderBody(): React.ReactNode {
    const files = this.getValue()
    return _.isEmpty(files) ? this.renderEmptyState() : this.renderChipList(files)
  }

  // Edit comes from the shell (see `prefilledEditText`), which places it
  // inline with the read-only content instead of floating it over the
  // thumbnail where it aligned with nothing.
  private renderReadOnly = (_value: any, { editNode }: { editNode?: React.ReactNode }): React.ReactNode => {
    const { showFileName } = this.props
    const files = this.getValue()
    const images = files.filter(isImageFileEntry)
    // Non-image files (PDFs, spreadsheets) have nothing for the image viewer
    // to show — routing them through it produced its grey "no image" box.
    // They read back as the same chips the editable body uses.
    const documents = files.filter((file) => !isImageFileEntry(file))
    const effectiveShowFileName = showFileName !== false
    return (
      <>
        <div className='ui2-fieldShell-readOnly-content'>
          <div className='ui2-fileUploadField-readOnly'>
            {!_.isEmpty(images) && (
              <UI2FileViewer
                {..._.omit(this.props as any, ['label', 'requiredType', 'tooltip', 'inlineHelpText', 'errors'])}
                showFileName={effectiveShowFileName}
                value={images}
              />
            )}
            {!_.isEmpty(documents) && (
              <div className='ui2-fileUploadField-chipRow'>
                {documents.map((file: any, index: number) => this.renderFileChip(file, index, false))}
              </div>
            )}
          </div>
        </div>
        {editNode}
      </>
    )
  }

  /**
   * Wording for the read-only empty state. Overridden by subclasses that
   * hold something other than files (photo capture holds images), and
   * overridden per-form by `emptyText` from uiSchema.
   */
  protected defaultEmptyReadOnlyText(): string {
    return DEFAULT_EMPTY_READ_ONLY_TEXT
  }

  /** Wording for the shell's prefilled Edit action. */
  protected defaultPrefilledEditText(): string {
    return DEFAULT_PREFILLED_EDIT_TEXT
  }

  protected renderEmptyState(): React.ReactNode {
    const isMobileWeb = this.context === 'mobileWeb'
    return isMobileWeb ? this.renderEmptyStateMobile() : this.renderEmptyStateDesktop()
  }

  protected renderEmptyStateDesktop(): React.ReactNode {
    const { placeholder, isDisabled } = this.props
    const { isDragging } = this.state
    const message = DEFAULT_EMPTY_MESSAGE_DW
    const buttonText = placeholder || DEFAULT_EMPTY_BUTTON_TEXT
    return (
      <>
        <div
          className={classNames('ui2-fileUploadField-dropZone', {
            'ui2-fileUploadField-dropZone--isDragging': isDragging,
            'ui2-fileUploadField-dropZone--isDisabled': isDisabled,
          })}
          onClick={isDisabled ? undefined : this.handleOpenPicker}
          onDragEnter={isDisabled ? undefined : this.handleDragEnter}
          onDragOver={isDisabled ? undefined : this.handleDragOver}
          onDragLeave={isDisabled ? undefined : this.handleDragLeave}
          onDrop={isDisabled ? undefined : this.handleDrop}
        >
          <div className='ui2-fileUploadField-dropZoneMessage'>{message}</div>
          <button
            type='button'
            className='ui2-fileUploadField-dropZoneButton'
            onClick={(e) => { e.stopPropagation(); this.handleOpenPicker() }}
            disabled={isDisabled}
          >
            <Icon icon={IconNames.UPLOAD} iconSize={14} />
            <span>{buttonText}</span>
          </button>
        </div>
        {this.renderHiddenInput()}
      </>
    )
  }

  protected renderEmptyStateMobile(): React.ReactNode {
    const { placeholder, isDisabled } = this.props
    const message = placeholder || DEFAULT_EMPTY_MESSAGE_MW
    return (
      <>
        <div
          role='button'
          tabIndex={isDisabled ? -1 : 0}
          className='ui2-fileUploadField-mobileEmpty'
          onClick={isDisabled ? undefined : this.handleOpenPicker}
          onKeyDown={isDisabled ? undefined : this.handleMobileEmptyKeyDown}
          aria-disabled={isDisabled || undefined}
        >
          <Icon icon={IconNames.UPLOAD} iconSize={16} />
          <span className='ui2-fileUploadField-mobileEmptyText'>{message}</span>
        </div>
        {this.renderHiddenInput()}
      </>
    )
  }

  protected handleMobileEmptyKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      this.handleOpenPicker()
    }
  }

  protected renderChipList(files: any[]): React.ReactNode {
    const { addMoreMessage, isDisabled, isReadOnly } = this.props
    const canAddMore = !isDisabled && !isReadOnly && !this.isAtMax(files.length)
    const canRemove = !isDisabled && !isReadOnly
    return (
      <div className='ui2-fileUploadField-chipRow'>
        {files.map((file: any, index: number) => this.renderFileChip(file, index, canRemove))}
        {canAddMore && (
          <button
            type='button'
            className='ui2-fileUploadField-chip ui2-fileUploadField-chip--add'
            onClick={this.handleOpenPicker}
          >
            <Icon icon={IconNames.PLUS} iconSize={12} />
            <span className='ui2-fileUploadField-chipAddLabel'>
              {addMoreMessage || DEFAULT_ADD_MORE_TEXT}
            </span>
          </button>
        )}
        {this.renderHiddenInput()}
      </div>
    )
  }

  // Shared by the editable body and the read-only document list. A disabled
  // field's chips are inert — no click target, no keyboard focus, no remove.
  protected renderFileChip(file: any, index: number, canRemove: boolean): React.ReactNode {
    const { isDisabled } = this.props
    const name = getFileEntryName(file)
    const canOpen = !isDisabled
    return (
      <span
        key={file?.uniqueId || `${name}-${index}`}
        role={canOpen ? 'button' : undefined}
        tabIndex={canOpen ? 0 : undefined}
        className={classNames('ui2-fileUploadField-chip', {
          'ui2-fileUploadField-chip--clickable': canOpen,
          'ui2-fileUploadField-chip--disabled': isDisabled,
        })}
        title={name}
        onClick={canOpen ? () => this.handleOpenFile(file) : undefined}
        onKeyDown={canOpen ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            this.handleOpenFile(file)
          }
        } : undefined}
      >
        <Icon
          className='ui2-fileUploadField-chipIcon'
          icon={IconNames.PAPERCLIP}
          iconSize={12}
        />
        <span className='ui2-fileUploadField-chipLabel'>{name}</span>
        {canRemove && (
          <button
            type='button'
            className='ui2-fileUploadField-chipRemove'
            onClick={(e) => { e.stopPropagation(); this.handleRemoveAtIndex(index) }}
            aria-label={`Remove ${name}`}
            title='Remove file'
          >
            <Icon icon={IconNames.CROSS} iconSize={12} />
          </button>
        )}
      </span>
    )
  }

  protected renderHiddenInput(): React.ReactNode {
    const { accept } = this.props
    const { max } = this.getEffectiveBounds()
    const allowMultiple = max == null || max > 1
    return (
      <input
        ref={this.inputRef}
        type='file'
        accept={accept}
        multiple={allowMultiple}
        className='ui2-fileUploadField-hiddenInput'
        onChange={this.handleFilesSelected}
      />
    )
  }

  private handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    this.dragDepth += 1
    if (!this.state.isDragging) this.setState({ isDragging: true })
  }

  private handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'
  }

  private handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    this.dragDepth = Math.max(0, this.dragDepth - 1)
    if (this.dragDepth === 0) this.setState({ isDragging: false })
  }

  private handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    this.dragDepth = 0
    this.setState({ isDragging: false })
    const dropped = e.dataTransfer?.files
    if (!dropped || dropped.length === 0) return
    this.ingestFiles(dropped)
  }

  protected getValue(): any[] {
    const { value } = this.props
    if (_.isArray(value)) return value
    const filesetFiles = _.get(value, 'files')
    if (_.isArray(filesetFiles)) return filesetFiles
    return []
  }

  protected getImageFiles(): any[] {
    return this.getValue().filter(isImageFileEntry)
  }

  protected getEffectiveBounds(): FileCountBounds {
    return resolveFileCountBounds(this.props)
  }

  protected isAtMax(count: number): boolean {
    const { max } = this.getEffectiveBounds()
    return max != null && count >= max
  }

  protected handleOpenPicker = () => {
    this.inputRef.current?.click()
  }

  private handleFilesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files
    const input = event.target
    if (fileList) this.ingestFiles(fileList)
    input.value = ''
  }

  // Mirrors the legacy `ui:fileInput` pipeline: mime detect → for images,
  // resize + WebP recompress → store as blob URL. Non-images pass through.
  protected async ingestFiles(fileList: FileList) {
    if (fileList.length === 0) return
    const { max } = this.getEffectiveBounds()
    const existing = this.getValue()
    const remaining = max != null ? Math.max(0, max - existing.length) : undefined
    const toIngest: File[] = []
    for (let i = 0; i < fileList.length; i++) {
      if (remaining != null && toIngest.length >= remaining) break
      const file = fileList.item(i)
      if (file) toIngest.push(file)
    }
    if (toIngest.length === 0) return

    const { maxImageDimension, imageQuality } = this.props
    const maxEdge = maxImageDimension ?? DEFAULT_MAX_SHORT_EDGE
    const quality = imageQuality ?? DEFAULT_WEBP_QUALITY

    const processed = await Promise.all(toIngest.map(async (originalFile) => {
      try {
        const mimeType = await getFileMimeType(originalFile)
        const isImage = /^image/.test(mimeType)
        const processedFile = isImage
          ? await convertToWebp(await resizeImageFile(originalFile, maxEdge), quality)
          : originalFile
        const uniqueId = uuidv4()
        const uri = URL.createObjectURL(processedFile)
        return {
          entry: {
            name: processedFile.name,
            type: getFileType(mimeType || processedFile.type || ''),
            size: processedFile.size,
            uri,
            uniqueId,
          },
          multipart: { file: processedFile, uniqueId },
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[ui2-fileUploadField] failed to process file', err)
        return null
      }
    }))

    const successful = processed.filter((p): p is NonNullable<typeof p> => p !== null)
    if (successful.length === 0) return

    this.registerMultipartFiles(successful.map((r) => r.multipart))
    this.props.onChange?.([...existing, ...successful.map((r) => r.entry)])
  }

  protected handleRemoveAtIndex = (index: number) => {
    const next = this.getValue().slice()
    const [removed] = next.splice(index, 1)
    const { entity } = this.props
    if (entity && removed?.uniqueId) {
      entity.removeMultipartFileByIds(removed.uniqueId)
    }
    this.props.onChange?.(next)
  }

  protected registerMultipartFiles(files: Array<{ file: File; uniqueId: string }>) {
    if (_.isEmpty(files)) return
    const { entity, frames } = this.props
    const valuePath = frames?.getContext?.('valuePath')
    if (!entity || !valuePath) return
    entity.addMultipartFiles(valuePath, files)
  }
}
